import React, { useEffect, useState } from 'react';
import { ShieldCheck, Info } from 'lucide-react';
import { ADMIN_SETTINGS, getAdminSetting, setAdminSetting } from '../../services/adminSettings';

/**
 * Admin Settings — the switches that turn admin-only tooling on and off in the
 * UI.
 *
 * These control what is SHOWN, not what is allowed. Each gated feature is still
 * restricted to the admin email on the server, so a switch here can only hide
 * something the admin already has; it can never grant it to anyone else.
 *
 * Rows come from ADMIN_SETTINGS, so adding a switch is a one-line change in
 * services/adminSettings.js.
 */

/** iOS-style on/off switch. */
const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900 ${
      checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-slate-600'
    }`}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const AdminSettings = () => {
  // One read on mount; the switches are the only thing that writes them.
  const [values, setValues] = useState(() =>
    Object.fromEntries(ADMIN_SETTINGS.map((setting) => [setting.key, getAdminSetting(setting.key)]))
  );
  const [justSaved, setJustSaved] = useState(null);

  const handleToggle = (key, next) => {
    setAdminSetting(key, next);
    setValues((prev) => ({ ...prev, [key]: next }));
    setJustSaved(key);
  };

  // Clear the "Saved" tick after a moment.
  useEffect(() => {
    if (!justSaved) return undefined;
    const timer = setTimeout(() => setJustSaved(null), 1800);
    return () => clearTimeout(timer);
  }, [justSaved]);

  return (
    <div className="p-6">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-1">
          <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/40">
            <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">Admin Settings</h1>
        </div>
        <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
          Show or hide admin-only tools inside the app. Turning something off only hides
          it for you — access is always enforced on the server.
        </p>

        <div className="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
          {ADMIN_SETTINGS.map((setting) => (
            <div key={setting.key} className="flex items-start justify-between gap-6 p-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-sm font-medium text-gray-900 dark:text-slate-100">
                    {setting.label}
                  </h2>
                  {setting.appliesTo && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400">
                      {setting.appliesTo}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
                  {setting.description}
                </p>
                {justSaved === setting.key && (
                  <p className="mt-1.5 text-xs text-green-600 dark:text-green-400">
                    Saved — {values[setting.key] ? 'now visible' : 'now hidden'}.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3 shrink-0 pt-0.5">
                <span
                  className={`text-xs font-medium w-8 text-right ${
                    values[setting.key]
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-400 dark:text-slate-500'
                  }`}
                >
                  {values[setting.key] ? 'On' : 'Off'}
                </span>
                <Toggle
                  checked={values[setting.key]}
                  onChange={(next) => handleToggle(setting.key, next)}
                  label={setting.label}
                />
              </div>
            </div>
          ))}
        </div>

        <p className="mt-4 flex items-start gap-1.5 text-xs text-gray-500 dark:text-slate-400">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            These preferences are saved in this browser, so they do not follow you to another
            machine.
          </span>
        </p>
      </div>
    </div>
  );
};

export default AdminSettings;
